import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import DateObject from "react-date-object";
import {
  SamplingSchedule,
  CreateScheduleData,
  getSchedules,
  createSchedule,
  updateSchedule,
  deleteSchedule,
} from '../../services/samplingScheduleService';

// Tab components
const SamplingScheduleTab: React.FC<{ type: 'home' | 'lab' }> = ({ type }) => {
  const [schedules, setSchedules] = useState<SamplingSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<SamplingSchedule | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<CreateScheduleData>({
    date: '',
    startTime: '08:00',
    endTime: '18:00',
    capacity: 10,
    isVacation: false,
    type: type,
  });
  
  // Persian date state
  const [selectedDate, setSelectedDate] = useState<DateObject | null>(null);

  // Load schedules
  useEffect(() => {
    fetchSchedules();
  }, [type]);

  const fetchSchedules = async () => {
    try {
      setLoading(true);
      const data = await getSchedules({ type });
      setSchedules(data);
    } catch (error: any) {
      toast.error(error.message || 'خطا در دریافت برنامه‌ها');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value,
    }));
  };

  // Handle Persian date change
  const handleDateChange = (date: DateObject | null) => {
    setSelectedDate(date);
    if (date) {
      // Convert Persian date to Gregorian format (YYYY-MM-DD)
      const jsDate = date.toDate(); // Convert to JavaScript Date
      const gregorianDate = jsDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
      setFormData(prev => ({
        ...prev,
        date: gregorianDate,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        date: '',
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate date selection
    if (!formData.date) {
      toast.error('لطفا تاریخ را انتخاب کنید');
      return;
    }
    
    try {
      if (editingSchedule) {
        // Update existing schedule
        await updateSchedule(editingSchedule._id, formData);
        toast.success('برنامه با موفقیت بروزرسانی شد');
      } else {
        // Create new schedule
        await createSchedule({ ...formData, type });
        toast.success('برنامه با موفقیت ایجاد شد');
      }
      
      // Reset form and refresh list
      resetForm();
      fetchSchedules();
    } catch (error: any) {
      toast.error(error.message || 'خطا در ذخیره برنامه');
    }
  };

  const handleEdit = (schedule: SamplingSchedule) => {
    setEditingSchedule(schedule);
    setFormData({
      date: schedule.date,
      startTime: schedule.startTime,
      endTime: schedule.endTime,
      capacity: schedule.capacity,
      isVacation: schedule.isVacation,
      type: schedule.type,
    });
    
    // Convert Gregorian date to Persian for the date picker
    const gregorianDate = new Date(schedule.date);
    const persianDate = new DateObject(gregorianDate).convert(persian, persian_fa);
    setSelectedDate(persianDate);
    
    setShowAddForm(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('آیا از حذف این برنامه اطمینان دارید؟')) {
      try {
        await deleteSchedule(id);
        toast.success('برنامه با موفقیت حذف شد');
        fetchSchedules();
      } catch (error: any) {
        toast.error(error.message || 'خطا در حذف برنامه');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      date: '',
      startTime: '08:00',
      endTime: '18:00',
      capacity: 10,
      isVacation: false,
      type: type,
    });
    setSelectedDate(null);
    setEditingSchedule(null);
    setShowAddForm(false);
  };

  // Helper function to format date for display in Persian
  const formatDateForDisplay = (date: string) => {
    try {
      const gregorianDate = new Date(date);
      const persianDate = new DateObject(gregorianDate).convert(persian, persian_fa);
      return persianDate.format('YYYY/MM/DD');
    } catch (error) {
      // Fallback to original date if conversion fails
      return date;
    }
  };

  return (
    <div className="mt-6">
      <div className="mb-4 flex justify-between items-center">
        <h3 className="text-lg font-medium">
          برنامه‌های نمونه‌گیری {type === 'home' ? 'در محل' : 'در آزمایشگاه'}
        </h3>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          {showAddForm ? 'بستن فرم' : '+ افزودن برنامه جدید'}
        </button>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="mb-6 bg-white rounded-lg shadow p-6">
          <h4 className="text-lg font-medium mb-4">
            {editingSchedule ? 'ویرایش برنامه' : 'افزودن برنامه جدید'}
          </h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                             <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">
                   تاریخ <span className="text-red-500">*</span>
                 </label>
                 <DatePicker
                   value={selectedDate}
                   onChange={handleDateChange}
                   calendar={persian}
                   locale={persian_fa}
                   format="YYYY/MM/DD"
                   placeholder="انتخاب تاریخ"
                   inputClass="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                   containerStyle={{
                     width: "100%"
                   }}
                 />
                 <p className="text-xs text-gray-500 mt-1">
                   تقویم شمسی - تاریخ را از تقویم انتخاب کنید
                 </p>
               </div>

              <div className="flex items-center">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="isVacation"
                    checked={formData.isVacation}
                    onChange={handleInputChange}
                    className="ml-2 h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">روز تعطیل</span>
                </label>
              </div>
            </div>

            {!formData.isVacation && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ساعت شروع <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ساعت پایان <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ظرفیت <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="capacity"
                    value={formData.capacity}
                    onChange={handleInputChange}
                    min="0"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
              >
                انصراف
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                {editingSchedule ? 'بروزرسانی' : 'ذخیره'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Schedules List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <p className="mt-2 text-gray-600">در حال بارگذاری...</p>
          </div>
        ) : schedules.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            هیچ برنامه‌ای ثبت نشده است.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  تاریخ
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  وضعیت
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ساعت شروع
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ساعت پایان
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ظرفیت
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  عملیات
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {schedules.map((schedule) => (
                <tr key={schedule._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDateForDisplay(schedule.date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {schedule.isVacation ? (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                        تعطیل
                      </span>
                    ) : (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        فعال
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {schedule.isVacation ? '-' : schedule.startTime}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {schedule.isVacation ? '-' : schedule.endTime}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {schedule.isVacation ? '-' : schedule.capacity}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(schedule)}
                      className="text-indigo-600 hover:text-indigo-900 ml-4"
                    >
                      ویرایش
                    </button>
                    <button
                      onClick={() => handleDelete(schedule._id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      حذف
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const Calendar: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'home' | 'lab'>('home');
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">مدیریت برنامه نمونه‌گیری</h1>
        
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px space-x-8">
            <button
              onClick={() => setActiveTab('home')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ml-8 ${
                activeTab === 'home'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              برنامه نمونه‌گیری در منزل
            </button>
            <button
              onClick={() => setActiveTab('lab')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'lab'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              برنامه نمونه‌گیری در آزمایشگاه
            </button>
          </nav>
        </div>
        
        <SamplingScheduleTab type={activeTab} />
        
                 <div className="mt-8 p-4 bg-blue-50 rounded-lg">
           <h3 className="text-sm font-medium text-blue-800 mb-2">راهنما:</h3>
           <ul className="text-sm text-blue-700 space-y-1">
             <li>• برای هر روز و نوع نمونه‌گیری فقط یک برنامه می‌توانید تعریف کنید</li>
             <li>• اگر روزی را به عنوان تعطیل انتخاب کنید، نیازی به تعیین ساعت و ظرفیت نیست</li>
             <li>• ساعت پایان باید بعد از ساعت شروع باشد</li>
             <li>• تقویم شمسی فعال است - تاریخ‌ها به صورت خودکار تبدیل می‌شوند</li>
             <li>• در جدول، تاریخ‌ها به تقویم شمسی نمایش داده می‌شوند</li>
           </ul>
         </div>
      </div>
    </div>
  );
};

export default Calendar;
